/**
 * Client-side applicability check for approach deployment.
 *
 * Performs a lightweight evaluation of UnifiedFilters against sphere properties
 * without making API calls. Used to set deploy button states on channel cards
 * without calling previewApproachDeployment per card.
 *
 * Only evaluates property-type filters client-side. Perspective/relationship
 * filters and arc-specific FilterExpressionTypes (children_count,
 * elapsed_in_stage, etc.) require server-side evaluation and are skipped
 * (treated as passing) — deliberately optimistic so the deploy button isn't
 * disabled for every perspective-lane approach pending a round trip.
 *
 * `arcFilters` is the one lane where this check is NOT optimistic: it
 * returns `false`, aligning with the server's fail-closed
 * `validateApplicability` answer. Arc applicability is common enough on
 * templates that leaving it optimistic would routinely surface a deploy
 * button that 400s on click.
 *
 * Full server-side validation always happens on deploy click.
 */

import { FilterExpression, FilterGroup, UnifiedFilters } from 'types/filters';
import type { SchemaProperty } from 'types/schema';
import { evaluateFilterExpression, evaluateFilterGroup } from './filterEvaluation';
import { isPropertyLeaf } from './filterGroupLeaf';

/**
 * Returns true if a FilterExpression can be evaluated client-side.
 * Only 'property' type (or unset type, which defaults to 'property') is evaluable.
 *
 * Exported so `applicabilityValidator.ts` can reuse the same predicate to
 * fail closed on non-property expressions server-side, rather than
 * maintaining a second copy of the "is this a property expression" rule.
 */
export function isPropertyExpression(expr: FilterExpression): boolean {
  return !expr.type || expr.type === 'property';
}

/**
 * Returns true if all leaves in a FilterGroup (and nested groups) are
 * property-type expressions and thus evaluable client-side. A lane leaf
 * (perspective/relationship/arc) requires graph traversal neither this
 * client-side check nor the synchronous server validator can do, so it
 * always makes a group non-fully-evaluable — same treatment as a
 * non-property `FilterExpression` (e.g. `children_count`).
 *
 * Exported for reuse by `applicabilityValidator.ts` (fail-closed guard).
 */
export function isGroupFullyEvaluable(group: FilterGroup): boolean {
  if (group.expressions?.some(leaf => (isPropertyLeaf(leaf) ? !isPropertyExpression(leaf) : true))) {
    return false;
  }
  if (group.groups?.some(sub => !isGroupFullyEvaluable(sub))) {
    return false;
  }
  return true;
}

/**
 * Lightweight client-side applicability check.
 *
 * Evaluates an approach's applicability filters against a sphere's properties
 * without server calls. Returns `true` when the sphere likely matches.
 *
 * Limitations (by design):
 * - perspectiveFilters are skipped (need graph traversal)
 * - relationshipFilters are skipped (need graph traversal — same contract as
 *   perspectiveFilters; the server-side validateApplicability fails closed on
 *   both, this optimistic pre-check passes both)
 * - arcFilters return `false` (see module doc — the one non-optimistic lane)
 * - Arc-specific FilterExpressionTypes in propertyFilters/filterGroups
 *   (children_count, etc.) are skipped
 * - filterGroups containing non-property expressions are skipped
 *
 * All skipped filters default to `true` (optimistic), except `arcFilters`
 * which returns `false`. The server performs the authoritative check on
 * deploy either way.
 *
 * @param applicability - The approach's UnifiedFilters (from approach metadata)
 * @param sphereContext - Sphere properties to evaluate against (e.g., { type: 'center_organization', label: 'Organization', name: 'Acme' })
 * @returns `true` if the sphere likely matches, `false` if it definitely does not
 */
export function checkClientSideApplicability(
  applicability: UnifiedFilters | null | undefined,
  sphereContext: Record<string, any>,
  schemaProperties: SchemaProperty[] = []
): boolean {
  if (!applicability) return true;

  const { propertyFilters, perspectiveFilters, arcFilters, filterGroups } = applicability;

  const hasPropertyFilters = propertyFilters && propertyFilters.length > 0;
  const hasPerspectiveFilters = perspectiveFilters && perspectiveFilters.length > 0;
  const hasArcFilters = arcFilters && arcFilters.length > 0;
  const hasFilterGroups = filterGroups && filterGroups.length > 0;

  if (!hasPropertyFilters && !hasPerspectiveFilters && !hasArcFilters && !hasFilterGroups) {
    return true;
  }

  // Arc filters require server-side evaluation; unlike perspective/relationship
  // this lane aligns with the server's fail-closed answer rather than being
  // skipped optimistically (see module + function doc above).
  if (hasArcFilters) {
    return false;
  }

  // Evaluate property filters (AND logic — all must match)
  if (hasPropertyFilters) {
    const evaluable = propertyFilters.filter(isPropertyExpression);
    for (const expr of evaluable) {
      if (!evaluateFilterExpression(expr, sphereContext, schemaProperties)) {
        return false;
      }
    }
  }

  // Evaluate filter groups — only groups where ALL expressions are property-type
  if (hasFilterGroups) {
    for (const group of filterGroups) {
      if (isGroupFullyEvaluable(group)) {
        if (!evaluateFilterGroup(group, sphereContext, schemaProperties)) {
          return false;
        }
      }
      // Non-fully-evaluable groups are skipped (optimistic)
    }
  }

  // perspectiveFilters and relationshipFilters are skipped — both require
  // server-side graph traversal (validateApplicability fails closed on them)
  return true;
}
